import React, { useState } from 'react';
import PeriodicReportingCreateModal from './PeriodicReporting/PeriodicReportingCreateModal';
import PeriodicReportingTable from './PeriodicReporting/PeriodicReportingTable';
import { useLocation } from 'react-router-dom-v5-compat';
import { getStatementsByProjectId } from 'lib/api_ts/periodicReportingApi';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loading } from '@components/atomic/atoms';

export type PeriodicReportingType = {
	id: number;
	periodicReportingNumber: string;
	startDate: string;
	endDate: string;
	contextAndObjectives?: string;
	expectedOutcomes?: string;
	projectPolicyRelevance?: string;
	workPerformedAndResults?: string;
	note?: string;
	sent: boolean;
	sentDate: string;
	approved: boolean;
	approvedDate: string;
	approvedAmount: number;
	cashedInAmount: number;
	cashedInDate: string;
	cashedIn: boolean;
	amount: number;
	monthsReportingEndDate: number;
	monthsReportingStartDate: number;
	monthsApprovedDate: number;
	monthsSentDate: number;
};

interface PeriodicReportingProps {
	state: 'DRAFT' | 'VALIDATED';
	isEnabledToView: boolean;
	userCompanies: number[];
	projectIndirectCostsPerc: number;
	isProjectWp: boolean;
}

const PeriodicReporting: React.FC<PeriodicReportingProps> = ({
	state,
	isEnabledToView,
	userCompanies,
	projectIndirectCostsPerc,
	isProjectWp,
}) => {
	const { pathname } = useLocation();
	const projectId = pathname.split('/')[2];

	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

	const queryClient = useQueryClient();

	const fetchData = () => {
		queryClient.invalidateQueries({ queryKey: ['periodicStatements', projectId] });
	};

	const { data: statements, isLoading } = useQuery({
		queryKey: ['periodicStatements', projectId, state],
		queryFn: async () => {
			const statementsFromApi = await getStatementsByProjectId(projectId, state);
			const formattedStatements = statementsFromApi.map((statement) => ({
				id: statement.id,
				periodicReportingNumber: statement.name,

				sent: statement.sent,
				sentDate: statement.sentDate,

				approvedAmount: statement.approvedAmount,

				approved: statement.approved,
				approvedDate: statement.approvedDate,

				cashedIn: statement.cashedIn,
				cashedInAmount: statement.cashedInAmount,
				cashedInDate: statement.cashedInDate,

				requestedAmount: statement.requestedAmount,
				indirectCost: statement.indirectCost,
				indirectInvoiceCost: statement.indirectInvoiceCost,

				startDate: statement.reportingStartDate,
				endDate: statement.reportingEndDate,
				contextAndObjectives: statement.contextAndObjectives,
				expectedOutcomes: statement.expectedOutcomes,
				projectPolicyRelevance: statement.projectPolicyRelevance,
				workPerformedAndResults: statement.workPerformedAndResults,
				note: statement.note,

				amount: statement.amount,
				monthsReportingStartDate: statement.monthsReportingStartDate,
				monthsReportingEndDate: statement.monthsReportingEndDate,
				monthsApprovedDate: statement.monthsApprovedDate,
				monthsSentDate: statement.monthsSentDate,
			}));
			return formattedStatements;
		},
		enabled: projectId !== undefined,
		refetchOnMount: false,
	});

	return isLoading ? (
		<Loading fixedHeight="50vh" />
	) : (
		<>
			<PeriodicReportingTable
				rowData={statements}
				setIsCreateModalOpen={setIsCreateModalOpen}
				fetchData={fetchData}
				state={state}
				isEnabledToView={isEnabledToView}
				isProjectWp={isProjectWp}
				userCompanies={userCompanies}
				projectIndirectCostsPerc={projectIndirectCostsPerc}
			/>
			<PeriodicReportingCreateModal isModalOpen={isCreateModalOpen} setIsModalOpen={setIsCreateModalOpen} fetchData={fetchData} />
		</>
	);
};

export default PeriodicReporting;
